import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * V databázi zůstávají tři stavy, v UI jen dva: „vyrizeno" (starší záznamy
 * a automatický požadavek, který se sám uzavřel po doplnění zipu) se ukazuje
 * jako Objednáno. Automat ho ale potřebuje odlišit — založí pak nový.
 */
type Stav = 'nova' | 'objednano' | 'vyrizeno';

type Pozadavek = {
  id: number;
  text: string;
  jmeno: string;
  stav: Stav;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
  zdroj: 'rucne' | 'automat';
  material_id: number | null;
};

/** Unix ms → „4.8.2026 12:53". */
function fmtDateTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const TRICET_DNI = 30 * 24 * 60 * 60 * 1000;

function Check({ className = '' }: { className?: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * Nákup — dashboard všeho, co je potřeba objednat: požadavky od kolegyň
 * z Nové objednávky i automatické požadavky u chybějících zipů. Jde o tytéž
 * záznamy, takže změna stavu tady je vidět i v Nové objednávce.
 */
export function NakupPage() {
  const [items, setItems] = useState<Pozadavek[] | null>(null);
  const [starsi, setStarsi] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: Pozadavek[] }>('/api/vyroba/pozadavky');
      setItems(d.items);
    } catch (e: unknown) {
      setItems([]);
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Načtení selhalo', kind: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function zmenitStav(p: Pozadavek, stav: 'nova' | 'objednano') {
    setItems((prev) =>
      (prev ?? []).map((x) => (x.id === p.id ? { ...x, stav, updated_at: Date.now() } : x)),
    );
    try {
      await apiFetch(`/api/vyroba/pozadavky/${p.id}`, { method: 'PATCH', body: { stav } });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Změna stavu selhala',
        kind: 'error',
      });
      await load();
    }
  }

  const now = Date.now();
  const ceka = (items ?? []).filter((p) => p.stav === 'nova');
  const objednaneVse = (items ?? [])
    .filter((p) => p.stav !== 'nova')
    .sort((a, b) => (b.updated_at ?? b.created_at) - (a.updated_at ?? a.created_at));
  const objednane = starsi
    ? objednaneVse
    : objednaneVse.filter((p) => now - (p.updated_at ?? p.created_at) < TRICET_DNI);
  const skrytych = objednaneVse.length - objednane.length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text">Nákup</h1>
          <p className="text-sm text-text-secondary mt-1">
            Všechno, co je potřeba objednat — požadavky z dílny (Nová objednávka) a automaticky
            chybějící zipy
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/vyroba/nova-objednavka"
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors"
          >
            Nová objednávka
          </Link>
          <Link
            to="/vyroba/nakup/material"
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors"
          >
            Sklad drobného materiálu
          </Link>
        </div>
      </div>

      {items === null ? (
        <p className="text-sm text-text-muted">Načítám…</p>
      ) : (
        <div className="space-y-6">
          <Sekce
            nadpis="Čeká na objednání"
            pocet={ceka.length}
            cls="border-warning"
            prazdne="Nic nečeká. 🎉"
            list={ceka}
            onStav={zmenitStav}
          />
          <Sekce
            nadpis={starsi ? 'Objednáno' : 'Objednáno (posledních 30 dní)'}
            pocet={objednane.length}
            cls="border-success"
            prazdne="Zatím nic."
            list={objednane}
            onStav={zmenitStav}
            pata={
              skrytych > 0 || starsi ? (
                <button
                  type="button"
                  onClick={() => setStarsi((s) => !s)}
                  className="w-full px-5 py-2.5 text-sm font-semibold text-primary hover:bg-surface-hover cursor-pointer border-t border-border-light"
                >
                  {starsi ? 'Skrýt starší' : `Zobrazit starší (${skrytych})`}
                </button>
              ) : null
            }
          />
        </div>
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

function Sekce({
  nadpis,
  pocet,
  cls,
  prazdne,
  list,
  onStav,
  pata,
}: {
  nadpis: string;
  pocet: number;
  cls: string;
  prazdne: string;
  list: Pozadavek[];
  onStav: (p: Pozadavek, stav: 'nova' | 'objednano') => void;
  pata?: React.ReactNode;
}) {
  return (
    <section className={`bg-surface rounded-xl shadow-card border-l-4 overflow-hidden ${cls}`}>
      <h2 className="px-5 py-3 text-base font-bold text-text border-b border-border-light">
        {nadpis} <span className="text-text-muted font-semibold">({pocet})</span>
      </h2>
      {list.length === 0 ? (
        <p className="px-5 py-4 text-sm text-text-muted">{prazdne}</p>
      ) : (
        <ul>
          {list.map((p) => {
            const objednano = p.stav !== 'nova';
            return (
              <li
                key={p.id}
                className={`flex items-start gap-3 px-5 py-3 border-b border-border-light last:border-0 flex-wrap sm:flex-nowrap ${
                  objednano ? 'bg-success-light' : ''
                }`}
              >
                <span className="w-5 shrink-0 pt-0.5 text-success">{objednano && <Check />}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text whitespace-pre-wrap break-words">
                    {p.text}
                  </div>
                  <div className="text-xs text-text-muted mt-1 flex items-center gap-2 flex-wrap">
                    {p.zdroj === 'automat' ? (
                      <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary font-semibold">
                        ⚙️ Automaticky — sklad zipů
                      </span>
                    ) : (
                      <span>{p.jmeno}</span>
                    )}
                    <span>· {fmtDateTime(p.created_at)}</span>
                    {p.updated_at && p.updated_by && (
                      <span>
                        · změnil(a) {p.updated_by} {fmtDateTime(p.updated_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => objednano && onStav(p, 'nova')}
                    className={`text-xs font-semibold rounded-md px-2.5 py-1.5 border cursor-pointer transition-colors ${
                      !objednano
                        ? 'bg-warning text-white border-warning'
                        : 'bg-surface text-text-secondary border-border hover:border-warning'
                    }`}
                  >
                    Čeká
                  </button>
                  <button
                    type="button"
                    onClick={() => !objednano && onStav(p, 'objednano')}
                    className={`inline-flex items-center gap-1 text-xs font-semibold rounded-md px-2.5 py-1.5 border cursor-pointer transition-colors ${
                      objednano
                        ? 'bg-success text-white border-success'
                        : 'bg-surface text-text-secondary border-border hover:border-success'
                    }`}
                  >
                    <Check className={objednano ? 'text-white' : 'text-success'} />
                    Objednáno
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {pata}
    </section>
  );
}
